import * as React from 'react';
import Media from 'react-media';

import classNames from 'classnames';

import { Typography } from '@coursera/cds-core';

import type { UserAgent } from 'bundles/item-lti/constants/types';
import { MOBILE_BREAKPOINT_PX } from 'bundles/page-footer/components/global-footer/constants';
import MobileBreadCrumb from 'bundles/page/components/MobileBreadCrumb';
import DdpBreadcrumbsContainer from 'bundles/premium-certificate/components/DdpBreadcrumbsContainer';
import type { BreadcrumbLink } from 'bundles/premium-certificate/constants/propTypes';

import 'css!./__styl__/DegreeHeaderCms';

type Props = {
  bannerImage: string;
  degreeName: string;
  marketedProgramName?: string;
  breadcrumbLinks?: Array<BreadcrumbLink>;
  fadeOnScroll?: boolean;
  partnerInfo: {
    rectangularLogo: string;
    secondaryLogo?: string;
    altText: string;
  };
  gradientColors?: Array<string>;
  isDegreeHome?: boolean;
  hasUserLangCodeMismatch?: boolean;
  userAgent: UserAgent;
};

type State = {
  headerOpacity: number;
  isFixed: boolean;
  offsetTop: number;
};

class DegreeHeaderCms extends React.Component<Props, State> {
  el: HTMLElement | null | undefined;

  offsetTop: number | undefined;

  state = {
    headerOpacity: 1,
    isFixed: false,
    offsetTop: 65,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll);
    this.onScroll();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    const { fadeOnScroll } = this.props;

    if (!fadeOnScroll) {
      this.setState({ headerOpacity: 1 });
      return;
    }

    const { scrollY } = window;
    const { el } = this;

    const maxScrollTop = (el && el.clientHeight) || 0;
    const { offsetTop } = this.state;
    const percent = Math.min(1, scrollY / maxScrollTop);
    const clampedPercent = percent / 1.5;

    this.setState({
      headerOpacity: 1 - clampedPercent,
      isFixed: scrollY >= offsetTop,
    });
  };

  getBackgroundStyle() {
    return {
      background: this.buildLinearGradient(),
    };
  }

  getContainerStyle() {
    const { bannerImage } = this.props;
    const { headerOpacity } = this.state;

    let containerStyles;

    if (typeof bannerImage !== 'undefined') {
      containerStyles = {
        backgroundImage: `url(${bannerImage})`,
        opacity: headerOpacity,
      };
    } else {
      containerStyles = {
        opacity: 0,
      };
    }

    return containerStyles;
  }

  getGradientStyle() {
    return {
      background: this.buildLinearGradient(),
    };
  }

  getDegreeName = () => {
    const { degreeName } = this.props;
    const maxLength = 80;

    let trimmedDegreeName = degreeName.substr(0, maxLength);

    trimmedDegreeName = trimmedDegreeName.substr(
      0,
      Math.min(trimmedDegreeName.length, trimmedDegreeName.lastIndexOf(' '))
    );
    const showTrimmedName = degreeName.length > maxLength;

    return showTrimmedName ? `${trimmedDegreeName}...` : degreeName;
  };

  buildLinearGradient() {
    const { gradientColors } = this.props;
    const defaultGradientColors = ['#213d79', '#0b1427'];
    const firstGradientColor = (gradientColors && gradientColors[1]) || defaultGradientColors[1];
    const secondGradientColor = (gradientColors && gradientColors[0]) || defaultGradientColors[0];

    return `linear-gradient(324.97deg, ${firstGradientColor} 0%, ${secondGradientColor} 100%)`;
  }

  render() {
    const {
      partnerInfo: { rectangularLogo, secondaryLogo, altText },
      isDegreeHome,
      marketedProgramName,
      breadcrumbLinks,
      hasUserLangCodeMismatch,
      userAgent,
    } = this.props;
    const { isFixed } = this.state;
    const degreeHeaderCmsClassname = classNames('rc-DegreeHeaderCms', {
      fixed: isFixed && !isDegreeHome,
      'no-banner': !hasUserLangCodeMismatch,
    });

    return (
      <div className={classNames('degree-header-cms-sticky-wrapper', { banner: hasUserLangCodeMismatch && isFixed })}>
        <div
          className={degreeHeaderCmsClassname}
          style={this.getBackgroundStyle()}
          ref={(el) => {
            this.el = el;
          }}
        >
          <div className="header-container">
            <div className="gradient-background" style={this.getGradientStyle()} />
            <div className="background-image" style={this.getContainerStyle()} />

            <div className="content-container bt3-container horizontal-box">
              <div className="content bt3-col-xs-12 bt3-col-sm-12 bt3-col-md-12 bt3-col-lg-12">
                {Array.isArray(breadcrumbLinks) && breadcrumbLinks.length > 1 && (
                  <Media
                    query={{ minWidth: MOBILE_BREAKPOINT_PX }}
                    defaultMatches={userAgent && !userAgent.isMobileBrowser}
                  >
                    {(matches) => {
                      if (!matches) {
                        const { label, href } = breadcrumbLinks[1];
                        return <MobileBreadCrumb href={href} label={label} />;
                      }
                      return <DdpBreadcrumbsContainer links={breadcrumbLinks} />;
                    }}
                  </Media>
                )}
                <Typography variant="d2" className="degree-name" color="invertBody">
                  {marketedProgramName || this.getDegreeName()}
                </Typography>
                <img src={rectangularLogo} alt={altText} className="degree-logo" />
                {secondaryLogo && <img src={secondaryLogo} alt={altText} className="degree-logo secondary-logo" />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DegreeHeaderCms;
